import React, { useCallback, useEffect, useMemo, useState } from "react";
import AsyncAutocomplete from "components/Input/AsyncAutocomplete/AsyncAutocomplete";
import { usePayfacOptionsCache } from "utils/optionsCache/payfacOptionsCache";

const PayfacPrefixSearchField = ({
  label,
  value,
  onChangeValue,
  disabled = false,
  sx,
}) => {
  const { fetchPayfacOptions } = usePayfacOptionsCache();
  const [selectedOption, setSelectedOption] = useState(null);
  const [preloadedOptions, setPreloadedOptions] = useState([]);
  const [hasMoreOptions, setHasMoreOptions] = useState(true);

  const fetchOptions = useCallback(async (inputValue) => {
    const keyword = `${inputValue || ""}`.trim();
    const { options, hasMoreOptions: nextHasMoreOptions } =
      await fetchPayfacOptions(keyword);

    setHasMoreOptions(nextHasMoreOptions);

    if (!keyword) {
      setPreloadedOptions(options);
    }

    return options;
  }, [fetchPayfacOptions]);

  useEffect(() => {
    if (disabled) return;
    fetchOptions("");
  }, [disabled, fetchOptions]);

  const normalizedValue = useMemo(() => `${value || ""}`.trim(), [value]);

  useEffect(() => {
    if (!normalizedValue) {
      setSelectedOption(null);
      return;
    }

    const matchedOption = preloadedOptions.find(
      (option) => option.value === normalizedValue,
    );
    setSelectedOption(
      matchedOption || {
        key: normalizedValue,
        value: normalizedValue,
        title: normalizedValue,
      },
    );
  }, [normalizedValue, preloadedOptions]);

  const handleChange = (event, newValue) => {
    setSelectedOption(newValue);
    onChangeValue?.(newValue?.value || "");
  };

  return (
    <AsyncAutocomplete
      label={label}
      fetchOptions={fetchOptions}
      preloadedOptions={preloadedOptions}
      hasMoreOptions={hasMoreOptions}
      useLocalFilter
      value={selectedOption}
      onChange={handleChange}
      getOptionLabel={(option) => option?.title || ""}
      isOptionEqualToValue={(option, selected) =>
        option?.value === selected?.value
      }
      disabled={disabled}
      sx={sx}
    />
  );
};

export default PayfacPrefixSearchField;
